
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Mic, Camera, FileText, Tag, Clock, BadgeCheck, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface VoiceResult {
  text: string;
  confidence: number;
  language: string;
  emotion: string;
  duration: number;
}

interface ImageResult {
  objects: string[];
  confidence: number;
  description: string;
  tags: string[];
}

interface LocationState {
  type: "voice" | "image";
  results: VoiceResult | ImageResult;
}

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | null;
  
  useEffect(() => {
    if (!state) {
      navigate("/dashboard");
    }
  }, [state, navigate]);

  if (!state) {
    return null;
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-2xl">
      <Button 
        variant="ghost" 
        className="mb-4 flex items-center gap-2"
        onClick={() => navigate("/dashboard")}
      >
        <ArrowLeft size={16} />
        <span>Back to Dashboard</span>
      </Button>
      
      <Card className="border-t-4 border-t-brand-500">
        <CardHeader>
          <div className="flex items-center justify-center mb-2">
            <div className="bg-brand-100 p-3 rounded-full">
              {state.type === "voice" ? (
                <Mic className="h-8 w-8 text-brand-500" />
              ) : (
                <Camera className="h-8 w-8 text-brand-500" />
              )}
            </div>
          </div>
          <CardTitle className="text-center text-2xl">
            {state.type === "voice" ? "Voice Analysis Results" : "Image Analysis Results"}
          </CardTitle>
          <CardDescription className="text-center">
            Here's what we found from your {state.type === "voice" ? "recording" : "image"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-center mb-4">
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 flex items-center gap-1 px-3 py-1">
              <BadgeCheck size={14} />
              <span>Analysis Complete</span>
            </Badge>
          </div>
          
          {state.type === "voice" ? (
            <VoiceResults results={state.results as VoiceResult} />
          ) : (
            <ImageResults results={state.results as ImageResult} />
          )}
        </CardContent>
        <CardFooter className="flex justify-center gap-4 pt-4">
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            <span>Back to Dashboard</span>
          </Button>
          <Button
            onClick={() => navigate(state.type === "voice" ? "/voice-record" : "/image-capture")}
            className="bg-brand-500 hover:bg-brand-600 flex items-center gap-2"
          >
            {state.type === "voice" ? <Mic size={16} /> : <Camera size={16} />}
            <span>New {state.type === "voice" ? "Recording" : "Capture"}</span>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

const VoiceResults = ({ results }: { results: VoiceResult }) => {
  return (
    <div className="space-y-4">
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <FileText size={18} className="text-brand-600" />
          <h3 className="font-medium">Transcription</h3>
        </div>
        <p className="text-gray-700">{results.text}</p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <BadgeCheck size={18} className="text-brand-600" />
            <h3 className="font-medium">Confidence</h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-brand-500 h-2.5 rounded-full" 
                style={{ width: `${results.confidence * 100}%` }}
              ></div>
            </div>
            <span className="text-sm font-medium">{Math.round(results.confidence * 100)}%</span>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={18} className="text-brand-600" />
            <h3 className="font-medium">Duration</h3>
          </div>
          <p className="text-gray-700">{Math.floor(results.duration / 60)}:{(results.duration % 60).toString().padStart(2, '0')}</p>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={18} className="text-brand-600" />
            <h3 className="font-medium">Emotion</h3>
          </div>
          <p className="text-gray-700">{results.emotion}</p>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Tag size={18} className="text-brand-600" />
            <h3 className="font-medium">Language</h3>
          </div>
          <p className="text-gray-700">{results.language}</p>
        </div>
      </div>
    </div>
  );
};

const ImageResults = ({ results }: { results: ImageResult }) => {
  return (
    <div className="space-y-4">
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <FileText size={18} className="text-brand-600" />
          <h3 className="font-medium">Description</h3>
        </div>
        <p className="text-gray-700">{results.description}</p>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <BadgeCheck size={18} className="text-brand-600" />
            <h3 className="font-medium">Confidence</h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-brand-500 h-2.5 rounded-full" 
                style={{ width: `${results.confidence * 100}%` }}
              ></div>
            </div>
            <span className="text-sm font-medium">{Math.round(results.confidence * 100)}%</span>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Tag size={18} className="text-brand-600" />
            <h3 className="font-medium">Objects Detected</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {results.objects.map((object, index) => (
              <Badge key={index} variant="secondary" className="bg-brand-100 text-brand-800 border-none">
                {object}
              </Badge>
            ))}
          </div>
        </div>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Tag size={18} className="text-brand-600" />
          <h3 className="font-medium">Tags</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {results.tags.map((tag, index) => (
            <Badge key={index} variant="outline" className="bg-white">
              {tag}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Results;
